import { useMemo } from 'react'
import Icon from './Icon'

const DAY_MS = 86400000

type TimelineProps = {
  timeMs: number
  onTimeChange: (ms: number) => void
  playing: boolean
  onTogglePlay: () => void
  onScrubStart?: () => void
  className?: string
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max)
}

export default function Timeline({
  timeMs,
  onTimeChange,
  playing,
  onTogglePlay,
  onScrubStart,
  className = '',
}: TimelineProps) {
  const now = Date.now()
  const rangeValue = useMemo(() => {
    const deltaDays = (timeMs - now) / DAY_MS
    return clamp(deltaDays, -180, 180)
  }, [timeMs, now])

  const targetDate = new Date(timeMs)
  const formattedDate = targetDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const classes = [
    'fixed md:static left-1/2 md:left-auto bottom-4 md:bottom-auto -translate-x-1/2 md:translate-x-0 z-30',
    'flex flex-col gap-3',
    'px-4 py-3 md:px-6 md:py-4',
    'rounded-2xl shadow-lg',
    'bg-black/60 backdrop-blur',
    'border border-white/10',
    'w-[min(92vw,840px)] md:w-[min(720px,100%)]',
    'pb-[calc(env(safe-area-inset-bottom)+12px)] md:pb-4',
    'md:mx-auto',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const handleStep = (days: number) => {
    const next = clamp(rangeValue + days, -180, 180)
    onTimeChange(now + next * DAY_MS)
  }

  const handleToday = () => {
    onTimeChange(now)
  }

  return (
    <footer className={classes}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => handleStep(-1)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-neutral-300 transition hover:border-sky-500/60 hover:bg-sky-500/10 hover:text-sky-200"
            title="Step back one day"
          >
            <Icon name="step-back" />
          </button>
          <button
            type="button"
            onClick={onTogglePlay}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-sky-500/20 text-sky-100 transition hover:border-sky-500 hover:bg-sky-500/30 drop-glow"
            title={playing ? 'Pause playback' : 'Play playback'}
          >
            <Icon name={playing ? 'pause' : 'play'} className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={() => handleStep(1)}
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 text-neutral-300 transition hover:border-sky-500/60 hover:bg-sky-500/10 hover:text-sky-200"
            title="Step forward one day"
          >
            <Icon name="step-forward" />
          </button>
        </div>
        <button
          type="button"
          onClick={handleToday}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-neutral-300 transition hover:border-sky-400/60 hover:bg-sky-500/10 hover:text-sky-100"
        >
          <Icon name="today" />
          Today
        </button>
      </div>
      <div className="flex items-center gap-4">
        <input
          type="range"
          min={-180}
          max={180}
          value={rangeValue}
          onMouseDown={onScrubStart}
          onTouchStart={onScrubStart}
          onChange={(event) => {
            const next = clamp(Number(event.target.value), -180, 180)
            onTimeChange(now + next * DAY_MS)
          }}
          className="w-full accent-sky-400"
        />
        <div
          className="text-sm text-neutral-400"
          title={`${formattedDate} (${rangeValue.toFixed(0)} days from now)`}
        >
          {formattedDate}
        </div>
      </div>
    </footer>
  )
}
